import { Contract, EPageViewType, OohPrice } from '../../types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { Button } from '@portal_components/components/Button';
import { FormCard } from '@portal_components/components/FormCard';
import { FormCheckbox } from '@portal_components/components/FormCheckbox';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormTimeField } from '@portal_components/components/FormTimeField';
import { TableHeadings } from '@portal_components/components/TableHeadings';
import { TableItem } from '@portal_components/components/TableItem';
import { TextInput } from '@portal_components/components/TextInput';

interface OutOfHoursProps {
  readonly: boolean;
  viewType: EPageViewType;
}

type StandardDays = {
  startTime: string;
  endTime: string;
  overrides?: Record<
    number,
    {
      startTime: string;
      endTime: string;
    }
  >;
};
const standardDaysIdToOoh: Record<string, StandardDays> = {
  '1': {
    startTime: '080000',
    endTime: '170000'
  },
  '2': {
    startTime: '080000',
    endTime: '173000'
  },
  '3': {
    startTime: '080000',
    endTime: '180000'
  },
  '4': {
    startTime: '080000',
    endTime: '180000',
    overrides: {
      '6': {
        startTime: '080000',
        endTime: '130000'
      }
    }
  }
};

export const OutOfHours = ({ readonly, viewType }: OutOfHoursProps) => {
  const {
    standingData: { standingDataStandardDaysList }
  } = useContractStandingData();

  const oohPricesFieldArray = useFieldArray<Contract, 'oohPrices'>({ name: 'oohPrices' });
  const futureDatedOohPricesFieldArray = useFieldArray<Contract, 'futureDatedOohPrices'>({
    name: 'futureDatedOohPrices'
  });

  const { watch, setValue, getValues } = useFormContext<Contract>();

  const standardDaysId = watch('standardDaysId');

  const daysOFWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((title, index) => {
    return {
      title,
      value: index + 1
    };
  });

  const updateOohForWorkingHours = () => {
    if (!standardDaysId) return;
    const defaultTimes = standardDaysIdToOoh[standardDaysId];
    if (!defaultTimes) return;

    const currentOohPrices = getValues('oohPrices') || [];

    const newOohPrices = currentOohPrices.reduce<OohPrice[]>((acc, oohPrice) => {
      if (oohPrice.contractOohId) {
        oohPrice.markedForDeletion = true;
        acc.push(oohPrice);
      }
      return acc;
    }, []);

    for (const day of daysOFWeek) {
      const overrideForDay = defaultTimes.overrides?.[day.value];
      if (['Saturday', 'Sunday'].includes(day.title) && !overrideForDay) {
        newOohPrices.push({
          fromTime: '000000',
          toTime: '235900',
          dayOfWeek: day.value
        });
        continue;
      }
      const startTime = overrideForDay?.startTime ?? defaultTimes.startTime;
      const endTime = overrideForDay?.endTime ?? defaultTimes.endTime;
      newOohPrices.push({ fromTime: '000000', toTime: startTime, dayOfWeek: day.value });
      newOohPrices.push({
        fromTime: endTime,
        toTime: '235900',
        dayOfWeek: day.value
      });
    }
    setValue('oohPrices', newOohPrices);
  };

  const oohTableItem = (oohPrice: OohPrice, index: number) => {
    if (oohPrice.markedForDeletion) return null;
    return (
      <TableItem readonly={readonly} key={`oohPrices-${index}`}>
        <FormDropdown<Contract> title="Day of Week" name={`oohPrices.${index}.dayOfWeek`} placeholder="Select..." elementsForDisplay={daysOFWeek} readonly mandatory hideErrorControl />
        <FormTimeField<Contract> title="From Time" name={`oohPrices.${index}.fromTime`} readonly mandatory hideErrorControl />
        <FormTimeField<Contract> title="To Time" name={`oohPrices.${index}.toTime`} readonly mandatory hideErrorControl />
        <FormTextInput<Contract> title="Hourly Base Price" name={`oohPrices.${index}.hourlyBasePrice`} readonly={readonly || oohPrice.markedForDeletion} mandatory hideErrorControl />
      </TableItem>
    );
  };

  const futureDatedOohTableItem = (futureDatedOohPrice: OohPrice, index: number) => {
    if (futureDatedOohPrice.markedForDeletion) return null;
    return (
      <TableItem key={`futureDatedOohPrice-${index}`} remove={() => futureDatedOohPricesFieldArray.remove(index)} readonly={readonly}>
        <FormDropdown<Contract> title="Day of Week" name={`futureDatedOohPrices.${index}.dayOfWeek`} placeholder="Select..." elementsForDisplay={daysOFWeek} readonly={readonly} hideErrorControl />
        <FormTimeField<Contract> title="From Time" name={`futureDatedOohPrices.${index}.fromTime`} readonly={readonly} hideErrorControl />
        <FormTimeField<Contract> title="To Time" name={`futureDatedOohPrices.${index}.toTime`} readonly={readonly} hideErrorControl />
        <FormTextInput<Contract> title="Hourly Base Price" name={`futureDatedOohPrices.${index}.hourlyBasePrice`} readonly={readonly} hideErrorControl />
        <FormDatePicker<Contract> title="Effective From Date" name={`futureDatedOohPrices.${index}.conOohPriceEfd`} readonly={readonly} hideErrorControl />
        {readonly && !futureDatedOohPrice.conOohPriceEtd && <TextInput title="Effective To Date" initialValue="None" readonly hideErrorControl />}
        {(!readonly || futureDatedOohPrice.conOohPriceEtd) && (
          <FormDatePicker<Contract> title="Effective To Date" name={`futureDatedOohPrices.${index}.conOohPriceEtd`} readonly={readonly} hideErrorControl />
        )}
      </TableItem>
    );
  };

  return (
    <div className="flex flex-col w-full">
      <FormCard title="Out of Hours Prices">
        <div className="flex flex-row w-full gap-4">
          <div className="w-1/2">
            <FormDropdown<Contract> title="Working Hours" name="standardDaysId" readonly={readonly} mandatory placeholder="Select..." elementsForDisplay={standingDataStandardDaysList} />
          </div>
          {!readonly && standardDaysId && (
            <div className="flex flex-row justify-start pb-7 pt-3">
              <Button text="Update OOH For Working Hours" primary={false} onClick={updateOohForWorkingHours} disabled={readonly} />
            </div>
          )}
        </div>
        {oohPricesFieldArray.fields.length > 0 && (
          <>
            <TableHeadings headings={['Day of Week', 'From', 'To', 'Hourly Base Price']} />
            {oohPricesFieldArray.fields.map(oohTableItem)}
          </>
        )}
        {oohPricesFieldArray.fields.length === 0 && <p className="text-center">Currently no out of hours prices</p>}
      </FormCard>
      {viewType !== EPageViewType.ADD && (
        <FormCard title="Future Dated Out of Hours Prices">
          {!readonly && <FormCheckbox title="Remove future dated Out of Hours prices" name="removeFutureDatedOohPrices" readonly={readonly} />}
          {futureDatedOohPricesFieldArray.fields.length > 0 && (
            <>
              <TableHeadings headings={['Day of Week', 'From', 'To', 'Hourly Base Price', 'Effective From Date', 'Effective To Date']} />
              {futureDatedOohPricesFieldArray.fields.map(futureDatedOohTableItem)}
            </>
          )}
          {futureDatedOohPricesFieldArray.fields.length === 0 && <p className="text-center">Currently no future dated OOH prices</p>}
        </FormCard>
      )}
    </div>
  );
};
